import React from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material'

const blogPosts = [
  {
    id: 1,
    title: 'The Complete Guide to Cigar Storage and Humidor Management',
    excerpt: 'Learn everything you need to know about proper cigar storage, humidor maintenance, and creating the perfect environment for your collection.',
    content: `Proper cigar storage is the foundation of any serious cigar collection. Without the right environment, even the finest cigars will deteriorate quickly, losing their flavor, aroma, and smokability.

**Understanding Humidity**

The ideal relative humidity for cigar storage is between 65% and 72%. Most aficionados aim for 70% RH, which mimics the tropical conditions where premium tobacco is grown and aged. Too much humidity encourages mold growth and makes cigars difficult to smoke, while too little causes them to dry out, crack, and burn harshly.

**Choosing a Humidor**

When selecting a humidor, look for one lined with Spanish cedar. This wood is prized for its ability to absorb and release moisture, helping to maintain stable humidity levels. It also imparts a subtle flavor to your cigars over time and repels tobacco beetles. Desktop humidors are great for collections of 25–100 cigars, while cabinet humidors can hold hundreds or thousands.

**Seasoning Your Humidor**

Before placing any cigars inside, you must season a new humidor. Wipe the interior cedar with distilled water using a clean sponge, place a shot glass filled with distilled water inside, and let it sit for 24–48 hours. Repeat as needed until the wood stabilizes at your target humidity.

**Maintaining Your Collection**

Check your humidor weekly. Rotate cigars periodically so all receive equal exposure to the humidity source. Keep the humidor away from direct sunlight and temperature extremes. With proper care, your humidor will protect your investment and continue improving your cigars for years to come.`,
    author: 'David O\'Hara',
    authorAvatar: 'DO',
    publishDate: '2024-01-15',
    readTime: '8 min read',
    category: 'Storage',
    featured: true
  },
  {
    id: 2,
    title: 'Understanding Cigar Ratings: A Beginner\'s Guide',
    excerpt: 'Discover how to properly rate and review cigars, what factors to consider, and how to develop your palate for better tasting experiences.',
    content: `Rating cigars is a deeply personal practice, yet there are universal principles that can help any smoker communicate their experiences more clearly and improve their palate over time.

**The Major Rating Systems**

Publications like Cigar Aficionado use a 100-point scale, while many enthusiasts create their own systems. Points are typically assigned across categories: appearance and construction, draw, burn, aroma, flavor, and finish. A score of 90 or above is generally considered outstanding, 80–89 is very good, and anything below 70 is poor.

**Assessing Construction**

Before lighting, examine the cigar. The wrapper should be smooth with minimal veins, and the color should be even. Give it a gentle squeeze along its length — it should feel uniformly packed without soft spots or hard lumps. A well-constructed cigar is the foundation of a great smoke.

**Evaluating the Draw**

After cutting, test the draw before lighting. It should offer slight resistance — like sipping a milkshake through a straw, not breathing through a cocktail straw. A plugged draw is a serious flaw; an overly loose draw produces too much smoke and heat.

**Developing Your Palate**

The key to improving as a taster is consistency and note-taking. Use CigarTrack to log your sessions with detailed flavor notes. Over time, you will start to recognize the differences between Nicaraguan and Dominican tobacco, identify the influence of different wrapper leaves, and build a vocabulary for what you experience.`,
    author: 'Sarah Chen',
    authorAvatar: 'SC',
    publishDate: '2024-01-12',
    readTime: '6 min read',
    category: 'Tasting',
    featured: false
  },
  {
    id: 3,
    title: 'Building Your First Cigar Collection: Tips for Beginners',
    excerpt: 'Starting a cigar collection can be overwhelming. Here\'s a comprehensive guide to help you build a diverse and enjoyable collection.',
    content: `Starting a cigar collection is one of the most rewarding journeys in the world of premium tobacco. The key is to approach it with curiosity rather than anxiety.

**Start with Variety**

Resist the urge to buy a box of one cigar right away. Instead, build a sampler of singles from different countries, wrapper types, and sizes. Try a Connecticut-wrapped mild cigar alongside a full-bodied Nicaraguan puro. Compare a robusto to a Churchill of the same blend. This exploration phase teaches you more than any guide can.

**Invest in Proper Storage Early**

Even if your collection is small, store it properly from day one. A quality desktop humidor in the 25–50 cigar range is an excellent starting point. Proper storage protects your investment and helps you understand how aging changes a cigar over months and years.

**Learn the Landscape**

Familiarize yourself with the major producing regions — Cuba, Nicaragua, Honduras, Dominican Republic, and Ecuador. Each imparts distinct characteristics to the tobacco grown there. Understanding geography helps you navigate shop recommendations and make informed purchases.

**Use CigarTrack to Guide Your Journey**

Logging every cigar you smoke is the fastest path to self-knowledge as a collector. Note what you liked, what fell flat, and which blends surprised you. Over time your own ratings and flavor notes become your most valuable reference.`,
    author: 'Michael Rodriguez',
    authorAvatar: 'MR',
    publishDate: '2024-01-10',
    readTime: '10 min read',
    category: 'Collection',
    featured: false
  },
  {
    id: 4,
    title: 'The Art of Cigar Pairing: Food, Drinks, and More',
    excerpt: 'Explore the world of cigar pairing and discover how different foods, drinks, and environments can enhance your smoking experience.',
    content: `Pairing a cigar with the right beverage or food can elevate both experiences, creating a sum greater than its parts. The principles are similar to wine and food pairing — balance, complement, and contrast.

**Spirits**

Whiskey and cigars share a natural affinity. A medium-bodied Honduran cigar pairs beautifully with a single malt Scotch, their earthy, smoky profiles reinforcing each other. A full-bodied Nicaraguan pairs well with a robust bourbon. For lighter, creamier cigars, consider aged rum or Cognac.

**Beer**

Craft beer offers surprising versatility. A mellow Connecticut-wrapped cigar finds harmony with a Belgian witbier, while a spicy, full-bodied blend can stand up to a rich imperial stout. The carbonation in beer also cleanses the palate between puffs in a way spirits cannot.

**Coffee**

Coffee and cigars are a classic pairing, sharing flavor compounds that complement each other. A mild to medium cigar pairs wonderfully with a creamy latte, while a bold espresso stands up to fuller-bodied smokes. Avoid pairing bitter, dark roasts with mild cigars — the coffee will overpower the tobacco.

**Food**

Rich, fatty foods like dark chocolate, aged cheese, and cured meats can soften the strength of a full-bodied cigar and amplify its complex flavors. Avoid spicy foods before smoking, as they can overwhelm your palate and diminish your ability to taste subtle notes.`,
    author: 'Jennifer Kim',
    authorAvatar: 'JK',
    publishDate: '2024-01-08',
    readTime: '7 min read',
    category: 'Pairing',
    featured: false
  },
  {
    id: 5,
    title: 'Cigar Aging: When and How to Age Your Cigars',
    excerpt: 'Learn about the aging process, optimal aging conditions, and how to determine when your cigars are ready to smoke.',
    content: `Aging cigars is a practice that separates patient collectors from those who smoke every cigar immediately. The rewards, when done correctly, are extraordinary.

**What Happens During Aging**

As a cigar rests in a properly humidified environment, the tobaccos continue to ferment and marry. Harshness mellows, complexity develops, and the flavors of the different filler, binder, and wrapper tobaccos integrate into a more cohesive profile. Ammonia — a natural byproduct of the fermentation process — dissipates, reducing the harsh bite sometimes found in freshly rolled cigars.

**Optimal Conditions**

Aging requires stable humidity (65–70% RH), stable temperature (65–70°F / 18–21°C), and darkness. Fluctuations are the enemy. A well-seasoned humidor in a temperature-controlled room is ideal. Cedar trays inside the humidor further support the process.

**What Ages Well**

Not all cigars benefit equally from aging. Full-bodied, complex blends with high-priming tobacco tend to improve most dramatically. Mild, light cigars with Connecticut wrappers can become too mellow and lose their character over time. In general, if a cigar is excellent fresh, it will likely be outstanding with five or more years of aging.

**Patience as Practice**

Set aside a box with the intention of not touching it for at least two years. Pull one cigar per year on a specific date and log your notes. This discipline teaches you more about how tobacco evolves than any book.`,
    author: 'David O\'Hara',
    authorAvatar: 'DO',
    publishDate: '2024-01-05',
    readTime: '9 min read',
    category: 'Aging',
    featured: false
  },
  {
    id: 6,
    title: 'Top 10 Cigar Brands Every Collector Should Know',
    excerpt: 'Discover the most prestigious and popular cigar brands, their history, and what makes them special in the world of premium cigars.',
    content: `The world of premium cigars is defined by a handful of legendary brands whose names are synonymous with quality, craftsmanship, and heritage.

**Arturo Fuente**

Based in the Dominican Republic, Fuente is perhaps the most beloved American cigar brand. Their Opus X line — made with a rare Dominican wrapper — redefined what New World cigars could achieve and remains one of the most sought-after smokes in the world.

**Padron**

The Padron family has been crafting Nicaraguan puro cigars since the 1960s. Their 1964 Anniversary and 1926 Series are considered benchmarks of full-bodied excellence, consistently earning top scores from major publications.

**Cohiba**

The most famous Cuban brand, Cohiba was originally created for Fidel Castro before being released to the public in 1982. Today both Cuban Cohiba and the Dominican Cohiba (produced by General Cigar for the US market) carry tremendous prestige.

**My Father Cigars**

Founded by José "Pepin" Garcia and his son Jaime, My Father has become one of the most decorated cigar brands of the 21st century. Their Nicaraguan blends are known for complexity, refinement, and impeccable construction.

**Davidoff**

The Swiss brand Davidoff represents the pinnacle of mild to medium-bodied luxury. Their meticulous standards — from seed selection to retail presentation — make them a standard-bearer for quality and elegance in the industry.

These five, among others like Oliva, Rocky Patel, Liga Privada, Montecristo, and Romeo y Julieta, form the foundation of any serious collector's education.`,
    author: 'Sarah Chen',
    authorAvatar: 'SC',
    publishDate: '2024-01-03',
    readTime: '12 min read',
    category: 'Brands',
    featured: false
  }
]

const BlogArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const post = blogPosts.find(p => String(p.id) === id)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (!post) {
    return (
      <Box>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
            color: 'white',
            py: { xs: 6, md: 8 }
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Article Not Found
            </Typography>
          </Container>
        </Box>
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            The article you are looking for does not exist.
          </Typography>
          <Button
            component={RouterLink}
            to="/blog"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderColor: '#8B4513',
              color: '#8B4513',
              '&:hover': {
                borderColor: '#7A3D0F',
                backgroundColor: 'rgba(139, 69, 19, 0.1)'
              }
            }}
          >
            Back to Blog
          </Button>
        </Container>
      </Box>
    )
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'white',
              mb: 3,
              opacity: 0.85,
              '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Blog
          </Button>
          <Chip
            label={post.category}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              mb: 2
            }}
          />
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
              lineHeight: 1.2
            }}
          >
            {post.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
            <Avatar sx={{ width: 48, height: 48, mr: 2, bgcolor: 'rgba(255,255,255,0.3)' }}>
              {post.authorAvatar}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                {post.author}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {formatDate(post.publishDate)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {post.readTime}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4, fontStyle: 'italic', lineHeight: 1.7 }}
        >
          {post.excerpt}
        </Typography>
        <Divider sx={{ mb: 4 }} />
        {post.content.split('\n\n').map((paragraph, index) => {
          if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
            return (
              <Typography
                key={index}
                variant="h5"
                component="h2"
                sx={{ color: '#8B4513', fontWeight: 600, mt: 4, mb: 2 }}
              >
                {paragraph.replace(/\*\*/g, '')}
              </Typography>
            )
          }
          // Handle inline bold within a paragraph
          const parts = paragraph.split(/(\*\*[^*]+\*\*)/)
          return (
            <Typography
              key={index}
              variant="body1"
              sx={{ mb: 2, lineHeight: 1.8, color: 'text.primary' }}
            >
              {parts.map((part, i) =>
                part.startsWith('**') && part.endsWith('**')
                  ? <strong key={i}>{part.replace(/\*\*/g, '')}</strong>
                  : part
              )}
            </Typography>
          )
        })}
        <Divider sx={{ mt: 6, mb: 4 }} />
        <Button
          component={RouterLink}
          to="/blog"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: '#8B4513',
            color: '#8B4513',
            '&:hover': {
              borderColor: '#7A3D0F',
              backgroundColor: 'rgba(139, 69, 19, 0.1)'
            }
          }}
        >
          Back to Blog
        </Button>
      </Container>
    </Box>
  )
}

export default BlogArticlePage
